import React, { useEffect, useState, useCallback } from "react";
import {
  FiUsers,
  FiHash,
  FiMessageSquare,
  FiActivity,
  FiLayers,
  FiTrendingUp,
  FiZap,
  FiUserCheck,
} from "react-icons/fi";
import { postRequestAuthenticated } from "../../../globals/imports.js";
import { SkeletonCards, SkeletonBlocks } from "../widgets/Skeleton";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const RANGES = [
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

const SOURCE_LABELS = {
  import: "Imported",
  onboard: "Self-joined",
  autologin: "Auto-login",
  google: "Google",
  otp: "Email",
  jwt: "SSO",
  trusted: "Trusted",
};

// Continuous YYYY-MM-DD list for the last N days (oldest -> newest).
const dayList = (days) => {
  const out = [];
  const now = Date.now();
  for (let i = days - 1; i >= 0; i--) {
    out.push(new Date(now - i * 86400000).toISOString().slice(0, 10));
  }
  return out;
};

const seriesToDaily = (arr, days) => {
  const map = {};
  (arr || []).forEach((d) => (map[d.date] = d.count));
  return dayList(days).map((date) => ({ label: date, value: map[date] || 0 }));
};

// Chunk the daily series into 7-day buckets (oldest-aligned).
const toWeekly = (daily) => {
  const weeks = [];
  for (let i = 0; i < daily.length; i += 7) {
    const chunk = daily.slice(i, i + 7);
    weeks.push({
      label: chunk[0].label,
      value: chunk.reduce((s, d) => s + d.value, 0),
    });
  }
  return weeks;
};

const Card = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-4">
    <div className="mb-2 flex items-center gap-2 text-theme-placeholder">
      <Icon size={15} />
      <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
    </div>
    <p className="text-2xl font-semibold text-theme-secondaryText">{value}</p>
  </div>
);

const BarChart = ({ data, accent }) => {
  const max = Math.max(1, ...data.map((d) => d.value));
  const total = data.reduce((s, d) => s + d.value, 0);
  const ticks = [0, Math.floor(data.length / 2), data.length - 1];
  return (
    <div>
      <div className="flex h-40 items-end gap-[2px]">
        {data.map((d, i) => (
          <div
            key={i}
            className="group relative flex flex-1 items-end"
            title={`${d.label}: ${d.value}`}
          >
            <div
              className="w-full rounded-t-sm transition-all"
              style={{
                height: `${(d.value / max) * 100}%`,
                minHeight: d.value > 0 ? 3 : 0,
                background: accent
                  ? "var(--theme-accent)"
                  : "var(--theme-emptyEvent)",
              }}
            />
            <span className="pointer-events-none absolute -top-6 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-theme-tertiaryBackground px-1.5 py-0.5 text-[10px] text-theme-secondaryText group-hover:block">
              {d.value}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-theme-placeholder">
        {ticks.map((t, i) => (
          <span key={i}>{data[t] ? data[t].label.slice(5) : ""}</span>
        ))}
      </div>
      {total === 0 && (
        <p className="mt-2 text-center text-xs text-theme-placeholder">
          No data in this window yet.
        </p>
      )}
    </div>
  );
};

// Fixed 7-bar chart for "activity by weekday" (labels under each bar).
const WeekdayBars = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.value));
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div>
      <div className="flex h-40 items-end gap-2">
        {data.map((d) => (
          <div key={d.label} className="flex flex-1 flex-col items-center gap-1">
            <div className="flex w-full flex-1 items-end" title={`${d.label}: ${d.value}`}>
              <div
                className="w-full rounded-t-sm transition-all"
                style={{
                  height: `${(d.value / max) * 100}%`,
                  minHeight: d.value > 0 ? 3 : 0,
                  background: "var(--theme-emptyEvent)",
                }}
              />
            </div>
            <span className="text-[10px] text-theme-placeholder">{d.label}</span>
          </div>
        ))}
      </div>
      {total === 0 && (
        <p className="mt-2 text-center text-xs text-theme-placeholder">
          No data in this window yet.
        </p>
      )}
    </div>
  );
};

const AnalyticsTab = () => {
  const [days, setDays] = useState(30);
  const [bucket, setBucket] = useState("day"); // day | week
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await postRequestAuthenticated("/business/analytics", { days: d });
      if (res?.success) setData(res);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(days);
  }, [days, load]);

  const totals = data?.totals || {};
  const acqDaily = seriesToDaily(data?.acquisition, days);
  const msgDaily = seriesToDaily(data?.messagesSeries, days);
  const acq = bucket === "week" ? toWeekly(acqDaily) : acqDaily;
  const msg = bucket === "week" ? toWeekly(msgDaily) : msgDaily;
  const sources = (data?.sources || []).filter((s) => s.source);
  const sourceMax = Math.max(1, ...sources.map((s) => s.count));
  const channels = data?.channels || [];
  const eng = data?.engagement || {};
  // Advanced analytics (Growth) — same server signal the other advanced blocks use.
  const advanced = !!data?.howMembersJoinedEnabled;
  const retention = data?.retention || {};
  const activeDaily = seriesToDaily(data?.activeSeries, days);
  const active = bucket === "week" ? toWeekly(activeDaily) : activeDaily;
  const weekdayMap = {};
  (data?.activityByWeekday || []).forEach((w) => (weekdayMap[w.weekday] = w.count));
  const weekdayData = WEEKDAYS.map((label, i) => ({
    label,
    value: weekdayMap[i + 1] || 0, // mongo $dayOfWeek: 1=Sun..7=Sat
  }));

  // First load (no data yet) — show skeletons instead of a flash of "—".
  if (!data) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-7 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-theme-secondaryText">
            Analytics
          </h1>
          <p className="mt-1 text-sm text-theme-placeholder">
            Growth and activity across your community.
          </p>
        </div>
        <div className="mb-6">
          <SkeletonCards count={5} cols="sm:grid-cols-5" />
        </div>
        <SkeletonBlocks count={2} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-7 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-theme-secondaryText">
            Analytics
          </h1>
          <p className="mt-1 text-sm text-theme-placeholder">
            Growth and activity across your community.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-theme-chatDivider p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                  days === r.days
                    ? "bg-theme-accent text-theme-accentText"
                    : "text-theme-primaryText hover:bg-theme-tertiaryBackground"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-lg border border-theme-chatDivider p-0.5">
            {["day", "week"].map((b) => (
              <button
                key={b}
                onClick={() => setBucket(b)}
                className={`rounded-md px-3 py-1 text-xs font-semibold capitalize transition-colors ${
                  bucket === b
                    ? "bg-theme-accent text-theme-accentText"
                    : "text-theme-primaryText hover:bg-theme-tertiaryBackground"
                }`}
              >
                {b}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Card icon={FiUsers} label="Members" value={totals.members ?? "—"} />
        <Card icon={FiHash} label="Channels" value={totals.channels ?? "—"} />
        <Card icon={FiLayers} label="Topics" value={totals.topics ?? "—"} />
        <Card icon={FiMessageSquare} label="Messages" value={totals.messages ?? "—"} />
        <Card icon={FiActivity} label="Active (7d)" value={totals.activeMembers7d ?? "—"} />
      </div>

      {/* Engagement summary — all plans; "posted" (participation) is Growth-only. */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card
          icon={FiActivity}
          label={`Active (${days}d)`}
          value={eng.activeMembersInPeriod ?? "—"}
        />
        <Card
          icon={FiTrendingUp}
          label="7-day active rate"
          value={eng.stickiness != null ? `${eng.stickiness}%` : "—"}
        />
        <Card
          icon={FiZap}
          label="Msgs / active member"
          value={eng.messagesPerActiveMember ?? "—"}
        />
        {advanced && (
          <Card
            icon={FiUserCheck}
            label="Members who've posted"
            value={`${data.participationRate ?? 0}%`}
          />
        )}
      </div>

      {/* Retention (Growth): D7/D30 activation-retention. Hidden on Basic. */}
      {advanced && (
        <div className="mb-6">
          <h3 className="mb-3 text-sm font-semibold text-theme-secondaryText">
            Retention
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              { label: "7-day", n: 7, pct: retention.d7 ?? 0, cohort: retention.cohort7 ?? 0 },
              { label: "30-day", n: 30, pct: retention.d30 ?? 0, cohort: retention.cohort30 ?? 0 },
            ].map((r) => (
              <div
                key={r.label}
                className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-4"
              >
                <div className="mb-2 flex items-center gap-2 text-theme-placeholder">
                  <FiTrendingUp size={15} />
                  <span className="text-xs font-medium uppercase tracking-wide">
                    {r.label}
                  </span>
                </div>
                <p className="text-2xl font-semibold text-theme-secondaryText">
                  {r.pct}%
                </p>
                <p className="mt-1 text-xs text-theme-placeholder">
                  of {r.cohort} members
                </p>
                <p className="mt-1 text-[11px] text-theme-placeholder">
                  % of new members who posted within their first {r.n} days
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
          <h3 className="mb-4 text-sm font-semibold text-theme-secondaryText">
            New members
          </h3>
          <BarChart data={acq} accent />
        </div>
        <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
          <h3 className="mb-4 text-sm font-semibold text-theme-secondaryText">
            Messages
          </h3>
          <BarChart data={msg} />
        </div>
      </div>

      {/* Advanced trends (Growth): active-members-per-day + weekday activity.
          Hidden on Basic (advanced === false). */}
      {advanced && (
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
            <h3 className="mb-4 text-sm font-semibold text-theme-secondaryText">
              Active members
            </h3>
            <BarChart data={active} accent />
          </div>
          <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
            <h3 className="mb-4 text-sm font-semibold text-theme-secondaryText">
              Activity by weekday
            </h3>
            <WeekdayBars data={weekdayData} />
          </div>
        </div>
      )}

      {/* Source breakdown + per-channel */}
      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* "How members joined" is advanced analytics (Growth) — gated by plan. */}
        {data?.howMembersJoinedEnabled && (
        <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
          <h3 className="mb-4 text-sm font-semibold text-theme-secondaryText">
            How members joined
          </h3>
          {sources.length === 0 ? (
            <p className="text-sm text-theme-placeholder">No members yet.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {sources
                .sort((a, b) => b.count - a.count)
                .map((s) => (
                  <div key={s.source}>
                    <div className="mb-1 flex justify-between text-xs">
                      <span className="text-theme-secondaryText">
                        {SOURCE_LABELS[s.source] || s.source}
                      </span>
                      <span className="text-theme-placeholder">{s.count}</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-theme-tertiaryBackground">
                      <div
                        className="h-full rounded-full bg-theme-accent"
                        style={{ width: `${(s.count / sourceMax) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
        )}

        <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
          <h3 className="mb-4 text-sm font-semibold text-theme-secondaryText">
            Per channel
          </h3>
          {channels.length === 0 ? (
            <p className="text-sm text-theme-placeholder">No channels yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-theme-chatDivider text-xs uppercase tracking-wide text-theme-placeholder">
                    <th className="py-2 pr-3 font-semibold">Channel</th>
                    <th className="py-2 pr-3 text-right font-semibold">Members</th>
                    <th className="py-2 text-right font-semibold">Messages</th>
                  </tr>
                </thead>
                <tbody>
                  {channels.slice(0, 12).map((c) => (
                    <tr
                      key={c._id}
                      className="border-b border-theme-chatDivider/60 last:border-b-0"
                    >
                      <td className="truncate py-2.5 pr-3 text-theme-secondaryText">
                        {c.name}
                      </td>
                      <td className="py-2.5 pr-3 text-right text-theme-placeholder">
                        {c.members}
                      </td>
                      <td className="py-2.5 text-right text-theme-placeholder">
                        {c.messages}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Advanced analytics (Growth): most-active members + topics. Empty on
          Basic (the endpoint returns [] there), so this whole block hides. */}
      {(data?.mostActiveUsers?.length > 0 ||
        data?.mostActiveTopics?.length > 0) && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {data?.mostActiveUsers?.length > 0 && (
            <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
              <h3 className="mb-4 text-sm font-semibold text-theme-secondaryText">
                Most active members
              </h3>
              <div className="flex flex-col gap-2.5">
                {data.mostActiveUsers.map((u) => (
                  <div
                    key={u._id}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="truncate text-sm text-theme-secondaryText">
                      {u.name || u.username || "Member"}
                    </span>
                    <span className="flex-shrink-0 text-xs text-theme-placeholder">
                      {u.count} msgs
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {data?.mostActiveTopics?.length > 0 && (
            <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
              <h3 className="mb-4 text-sm font-semibold text-theme-secondaryText">
                Most active topics
              </h3>
              <div className="flex flex-col gap-2.5">
                {data.mostActiveTopics.map((t) => (
                  <div
                    key={t._id}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="truncate text-sm text-theme-secondaryText">
                      {t.name}
                    </span>
                    <span className="flex-shrink-0 text-xs text-theme-placeholder">
                      {t.count} msgs
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {loading && (
        <p className="mt-4 text-center text-xs text-theme-placeholder">Updating…</p>
      )}
    </div>
  );
};

export default AnalyticsTab;
